import React, { useCallback, useState } from 'react';
import { Loader2, Calculator, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const money = (n) => (n === null || n === undefined ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

// T1 · read-only sweep: lines → markup → discounts → quote total → contract amount → GST → last PDF.
export default function QuoteMathsCheckPage() {
  const { api } = useAuth();
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const run = useCallback(async () => {
    setBusy(true);
    try { setReport((await api.get('/admin/quote-parity-sweep')).data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Sweep failed'); }
    setBusy(false);
  }, [api]);
  return (
    <div data-testid="quote-maths-check-page" className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Quote Maths Check</h1>
          <p className="text-sm text-slate-500">Read-only. For every quote: lines × markup − discounts = quote total ex GST = contract amount; incl GST = ex × 1.15; compared to the last saved PDF.</p>
        </div>
        <button data-testid="quote-maths-run-btn" onClick={run} disabled={busy}
          className="flex items-center gap-2 px-4 py-2 bg-[#D35400] text-white rounded-md text-sm font-semibold disabled:opacity-60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Calculator size={14} />} Run check
        </button>
      </div>
      {report && (
        <>
          <div data-testid="quote-maths-summary" className="flex gap-3 text-sm">
            <span className="px-3 py-1 rounded bg-slate-100">Checked {report.checked}</span>
            <span className="px-3 py-1 rounded bg-emerald-50 text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={14} /> OK {report.ok}</span>
            <span className="px-3 py-1 rounded bg-rose-50 text-rose-700 inline-flex items-center gap-1"><AlertTriangle size={14} /> Issues {report.with_issues}</span>
          </div>
          <div className="overflow-x-auto bg-white border border-slate-200 rounded-md">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                <tr>{['Quote', 'Kind', 'Lines', 'Markup', 'Disc %', 'Expected ex GST', 'Quote ex GST', 'Contract', 'Incl GST', 'Last PDF ex GST', 'Issues'].map(h => <th key={h} className="px-2 py-2 text-left">{h}</th>)}</tr>
              </thead>
              <tbody>
                {report.rows.map(r => (
                  <tr key={r.appraisal_id} data-testid={`quote-maths-row-${r.appraisal_no || r.appraisal_id}`} className={`border-t ${r.issues.length ? 'bg-rose-50/50' : ''}`}>
                    <td className="px-2 py-1.5"><a className="text-[#D35400] hover:underline" href={`/appraisals/${r.appraisal_id}?tab=Overview`} target="_blank" rel="noreferrer">A{r.appraisal_no || '—'}</a></td>
                    <td className="px-2 py-1.5">{r.kind}{r.legacy_access && <span data-testid={`quote-maths-legacy-${r.appraisal_no}`} className="ml-1 text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" title="Imported from Access — saved totals came from the old system; re-save the quote to refresh them from today's lines">Access import</span>}</td>
                    <td className="px-2 py-1.5 font-mono-num">{money(r.lines_total)}</td>
                    <td className="px-2 py-1.5">{r.markup}</td>
                    <td className="px-2 py-1.5">{r.discount_pct}</td>
                    <td className="px-2 py-1.5 font-mono-num">{money(r.expected_ex_gst)}</td>
                    <td className="px-2 py-1.5 font-mono-num">{money(r.quote_ex_gst)}</td>
                    <td className="px-2 py-1.5 font-mono-num">{money(r.contract_amount)}</td>
                    <td className="px-2 py-1.5 font-mono-num">{money(r.quote_inc_gst)}</td>
                    <td className="px-2 py-1.5 font-mono-num">{money(r.last_pdf_ex_gst)}</td>
                    <td className="px-2 py-1.5 text-rose-700">{r.issues.join(' · ') || <span className="text-emerald-600">OK</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
